/**
 * A well-formedness XML parser for the converter.
 *
 * It reads elements, attributes, text, CDATA, comments, processing instructions, the XML
 * declaration and a DOCTYPE (skipped), decodes the five predefined entities and numeric
 * character references, and reports the first well-formedness error with its line and
 * column. It does not validate against a DTD or schema, does not fetch anything, and does
 * not expand entities declared in a DOCTYPE: using one is an error, which is also what
 * makes entity-expansion attacks impossible.
 */

import {
  DataError,
  type DataWarning,
} from "@/lib/tool-kit/latex-dev/data-model";

export interface XmlElement {
  name: string;
  /** Attributes in the order written. */
  attrs: [string, string][];
  /** Child elements and text, in document order. */
  children: (XmlElement | string)[];
  line: number;
}

export interface XmlDocument {
  root: XmlElement;
  warnings: DataWarning[];
}

const MAX_DEPTH = 500;
const RE_NAME = /[\p{L}_:][\p{L}\p{N}_:.\-·]*/uy;
const RE_ENTITY = /&(#x[0-9a-fA-F]+|#[0-9]+|[^\s;&<]*);/y;
const RE_SPACE = /[ \t\r\n]+/y;
const RE_DECLARATION = /^<\?xml[ \t\r\n]/;
const RE_ATTR_BREAKS = /[\t\r\n]/g;

const PREDEFINED: Readonly<Record<string, string>> = {
  lt: "<",
  gt: ">",
  amp: "&",
  quot: '"',
  apos: "'",
};

function isXmlChar(code: number): boolean {
  return (
    code === 0x9 ||
    code === 0xa ||
    code === 0xd ||
    (code >= 0x20 && code <= 0xd7_ff) ||
    (code >= 0xe0_00 && code <= 0xff_fd) ||
    (code >= 0x1_00_00 && code <= 0x10_ff_ff)
  );
}

class XmlReader {
  private i = 0;
  private readonly src: string;
  readonly warnings: DataWarning[] = [];

  private readonly lineStarts: number[];

  constructor(src: string) {
    this.src = src;
    this.lineStarts = [0];
    for (let k = 0; k < src.length; k++) {
      if (src[k] === "\n") {
        this.lineStarts.push(k + 1);
      }
    }
  }

  /** 1-based line and column of an offset, by binary search over the line starts. */
  private position(at: number): { line: number; column: number } {
    const starts = this.lineStarts;
    let low = 0;
    let high = starts.length - 1;
    while (low < high) {
      const mid = Math.ceil((low + high) / 2);
      if (starts[mid] <= at) {
        low = mid;
      } else {
        high = mid - 1;
      }
    }
    return { line: low + 1, column: at - starts[low] + 1 };
  }

  private fail(
    code: string,
    at = this.i,
    params: Record<string, string | number> = {}
  ): never {
    const { line, column } = this.position(at);
    throw new DataError(code, line, column, params);
  }

  private lineAt(at: number): number {
    return this.position(at).line;
  }

  private skipSpace(): void {
    RE_SPACE.lastIndex = this.i;
    const match = RE_SPACE.exec(this.src);
    if (match) {
      this.i += match[0].length;
    }
  }

  private readName(): string {
    RE_NAME.lastIndex = this.i;
    const match = RE_NAME.exec(this.src);
    if (!match) {
      return this.fail("xmlBadName");
    }
    this.i += match[0].length;
    return match[0];
  }

  // -- entities and text ---------------------------------------------------------

  private decodeEntity(): string {
    RE_ENTITY.lastIndex = this.i;
    const match = RE_ENTITY.exec(this.src);
    if (!match) {
      return this.fail("xmlBadEntity", this.i, {
        text: this.src.slice(this.i, this.i + 8),
      });
    }
    const body = match[1];
    let text: string;
    if (body.startsWith("#")) {
      const code =
        body[1] === "x"
          ? Number.parseInt(body.slice(2), 16)
          : Number.parseInt(body.slice(1), 10);
      if (!isXmlChar(code)) {
        return this.fail("xmlBadEntity", this.i, { text: match[0] });
      }
      text = String.fromCodePoint(code);
    } else if (Object.hasOwn(PREDEFINED, body)) {
      text = PREDEFINED[body];
    } else {
      return this.fail("xmlUndefinedEntity", this.i, { name: body });
    }
    this.i += match[0].length;
    return text;
  }

  /** Character data up to the next `<`, with entities decoded. */
  private readText(): string {
    let out = "";
    while (this.i < this.src.length && this.src[this.i] !== "<") {
      if (this.src[this.i] === "&") {
        out += this.decodeEntity();
      } else {
        const code = this.src.codePointAt(this.i) ?? 0;
        if (!isXmlChar(code)) {
          this.fail("xmlBadChar", this.i, {
            code: `U+${code.toString(16).toUpperCase().padStart(4, "0")}`,
          });
        }
        const ch = String.fromCodePoint(code);
        out += ch;
        this.i += ch.length;
      }
    }
    return out;
  }

  // -- markup -----------------------------------------------------------------------

  private readAttributeValue(): string {
    const quote = this.src[this.i];
    if (quote !== '"' && quote !== "'") {
      return this.fail("xmlAttrQuote");
    }
    this.i++;
    let out = "";
    for (;;) {
      const c = this.src[this.i];
      if (c === undefined) {
        return this.fail("xmlUnexpectedEnd");
      }
      if (c === quote) {
        this.i++;
        return out.replace(RE_ATTR_BREAKS, " ");
      }
      if (c === "<") {
        return this.fail("xmlAttrLt");
      }
      if (c === "&") {
        out += this.decodeEntity();
      } else {
        out += c;
        this.i++;
      }
    }
  }

  private readAttributes(): {
    attrs: [string, string][];
    selfClosing: boolean;
  } {
    const attrs: [string, string][] = [];
    for (;;) {
      const before = this.i;
      this.skipSpace();
      const c = this.src[this.i];
      if (c === ">") {
        this.i++;
        return { attrs, selfClosing: false };
      }
      if (c === "/" && this.src[this.i + 1] === ">") {
        this.i += 2;
        return { attrs, selfClosing: true };
      }
      if (c === undefined) {
        return this.fail("xmlUnexpectedEnd");
      }
      if (this.i === before) {
        return this.fail("xmlBadName");
      }
      const at = this.i;
      const name = this.readName();
      this.skipSpace();
      if (this.src[this.i] !== "=") {
        return this.fail("xmlAttrEquals", this.i, { name });
      }
      this.i++;
      this.skipSpace();
      if (attrs.some(([existing]) => existing === name)) {
        this.fail("xmlAttrDuplicate", at, { name });
      }
      attrs.push([name, this.readAttributeValue()]);
    }
  }

  private skipUntil(end: string, code: string, from = this.i): void {
    const index = this.src.indexOf(end, from);
    if (index < 0) {
      // The opener is where the problem starts: this.i has not moved past it yet.
      this.fail(code);
    }
    this.i = index + end.length;
  }

  private skipDoctype(): void {
    const start = this.i;
    let depth = 0;
    this.i += 9;
    while (this.i < this.src.length) {
      const c = this.src[this.i];
      if (c === '"' || c === "'") {
        const close = this.src.indexOf(c, this.i + 1);
        this.i = close < 0 ? this.src.length : close + 1;
        continue;
      }
      if (c === "[") {
        depth++;
      } else if (c === "]") {
        depth--;
      } else if (c === ">" && depth <= 0) {
        this.i++;
        this.warnings.push({
          code: "doctypeIgnored",
          line: this.lineAt(start),
          params: {},
        });
        return;
      }
      this.i++;
    }
    this.fail("xmlUnexpectedEnd", start);
  }

  /** Skip comments, processing instructions and white space; stop at an element or end. */
  private skipMisc(allowDoctype: boolean): void {
    for (;;) {
      this.skipSpace();
      if (this.src.startsWith("<!--", this.i)) {
        this.skipUntil("-->", "xmlUnclosedComment", this.i + 4);
      } else if (this.src.startsWith("<?", this.i)) {
        this.skipUntil("?>", "xmlUnclosedPi", this.i + 2);
      } else if (allowDoctype && this.src.startsWith("<!DOCTYPE", this.i)) {
        this.skipDoctype();
      } else {
        return;
      }
    }
  }

  // -- elements -----------------------------------------------------------------------

  /**
   * Read one construct inside an element: a tag, a comment, CDATA, an instruction or
   * text. Returns the finished root when the last open element has just closed.
   */
  private readNode(stack: XmlElement[]): XmlElement | null {
    const top = stack.at(-1);
    if (this.src.startsWith("</", this.i)) {
      this.closeElement(stack);
      return null;
    }
    if (this.src.startsWith("<!--", this.i)) {
      this.skipUntil("-->", "xmlUnclosedComment", this.i + 4);
    } else if (this.src.startsWith("<![CDATA[", this.i)) {
      const start = this.i + 9;
      this.skipUntil("]]>", "xmlUnclosedCdata", start);
      top?.children.push(this.src.slice(start, this.i - 3));
    } else if (this.src.startsWith("<?", this.i)) {
      this.skipUntil("?>", "xmlUnclosedPi", this.i + 2);
    } else if (this.src[this.i] === "<") {
      const { element, selfClosing } = this.openElement(stack.length);
      top?.children.push(element);
      if (!selfClosing) {
        stack.push(element);
      }
      return element;
    } else if (top) {
      top.children.push(this.readText());
    }
    return null;
  }

  private readElement(): XmlElement {
    const stack: XmlElement[] = [];
    let root: XmlElement | null = null;
    while (root === null || stack.length > 0) {
      if (this.i >= this.src.length) {
        const open = stack.at(-1) as XmlElement;
        return this.fail("xmlUnclosedElement", this.i, {
          name: open.name,
          line: open.line,
        });
      }
      const opened = this.readNode(stack);
      root = root ?? opened;
    }
    return root;
  }

  private openElement(depth: number): {
    element: XmlElement;
    selfClosing: boolean;
  } {
    if (depth >= MAX_DEPTH) {
      this.fail("xmlTooDeep");
    }
    const start = this.i;
    this.i++;
    const name = this.readName();
    const { attrs, selfClosing } = this.readAttributes();
    return {
      element: { name, attrs, children: [], line: this.lineAt(start) },
      selfClosing,
    };
  }

  private closeElement(stack: XmlElement[]): void {
    const at = this.i;
    this.i += 2;
    const name = this.readName();
    this.skipSpace();
    if (this.src[this.i] !== ">") {
      this.fail("xmlBadName");
    }
    this.i++;
    const open = stack.pop();
    if (open && open.name !== name) {
      this.fail("xmlMismatchedTag", at, {
        found: name,
        expected: open.name,
        line: open.line,
      });
    }
  }

  parse(): XmlDocument {
    if (this.src.charCodeAt(0) === 0xfe_ff) {
      this.i = 1;
    }
    if (RE_DECLARATION.test(this.src.slice(this.i, this.i + 6))) {
      this.skipUntil("?>", "xmlUnclosedPi", this.i + 2);
    }
    this.skipMisc(true);
    if (this.i >= this.src.length) {
      return this.fail("xmlNoRoot");
    }
    if (this.src.startsWith("</", this.i)) {
      return this.fail("xmlStrayEndTag");
    }
    if (this.src[this.i] !== "<") {
      return this.fail("xmlTextOutside");
    }
    const root = this.readElement();
    this.skipMisc(false);
    if (this.i < this.src.length) {
      this.fail(
        this.src[this.i] === "<" ? "xmlMultipleRoots" : "xmlTextOutside"
      );
    }
    return { root, warnings: this.warnings };
  }
}

/** Parse XML text. Throws a `DataError` with the line and column of the first problem. */
export function parseXml(src: string): XmlDocument {
  return new XmlReader(src).parse();
}
